import React, { useEffect, useState } from 'react';
import { X, Download, FileText, Image as ImageIcon, Printer } from 'lucide-react';
import { licenseService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';

/**
 * Visualizador de arquivos (PDF/imagem) com download e impressão.
 */
export default function FileViewerModal({ anexo, onClose, loadBlob }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchBlob = loadBlob || (() => licenseService.getCondicionanteAnexoBlob(anexo.id));

  useEffect(() => {
    if (!anexo?.id && !loadBlob) return undefined;
    let revoked = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetchBlob();
        if (revoked) return;
        const url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      } catch (e) {
        if (!revoked) setError(userFacingError(e, 'loadError'));
      } finally {
        if (!revoked) setLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anexo?.id]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const mime = anexo?.mime_type || '';
  const isImage = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(anexo?.filename_original || '');
  const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(anexo?.filename_original || '');

  const handleDownload = async () => {
    try {
      const r = await fetchBlob();
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = anexo.filename_original || anexo.filename || 'anexo';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(userFacingError(e, 'downloadError'));
    }
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    const popup = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (!popup) {
      setError('Permita pop-ups para imprimir o arquivo.');
      return;
    }
    popup.addEventListener('load', () => {
      try {
        popup.focus();
        popup.print();
      } catch {
        /* o usuário imprime pelo visualizador do navegador */
      }
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {isImage ? (
              <ImageIcon className="h-5 w-5 shrink-0 text-primary-600" />
            ) : (
              <FileText className="h-5 w-5 shrink-0 text-primary-600" />
            )}
            <span className="truncate text-sm font-medium text-gray-900">
              {anexo?.filename_original || 'Anexo'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={handleDownload}>
              <Download className="h-4 w-4" />
              Baixar
            </button>
            <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={handlePrint}>
              <Printer className="h-4 w-4" />
              Imprimir
            </button>
            <button type="button" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" onClick={onClose} aria-label="Fechar">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading && <p className="text-sm text-gray-600">Carregando…</p>}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
          )}
          {!loading && !error && blobUrl && (
            <>
              {isImage && (
                <img src={blobUrl} alt={anexo.filename_original} className="mx-auto max-h-[70vh] max-w-full rounded-lg object-contain" />
              )}
              {isPdf && (
                <iframe title={anexo.filename_original} src={blobUrl} className="h-[70vh] w-full rounded-lg border border-gray-200" />
              )}
              {!isImage && !isPdf && (
                <div className="flex flex-col items-center gap-3 py-12 text-gray-600">
                  <FileText className="h-12 w-12 text-gray-400" />
                  <p className="text-sm">Visualização não disponível para este tipo de arquivo.</p>
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleDownload}>
                    Baixar arquivo
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
